import DesignTokens from '@tableau/design-tokens';
import { NotificationInfoOutlineIconRaw } from '@tableau/icons/NotificationInfoOutlineIcon/raw';
import { ReactiveRawIconRegistry } from '@tableau/icons/reactive';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { StyledCellFormatter } from '~src/spacesPlatform/components/ListView/StyledCellWrapper';
import { applyColor } from '~src/style/ColorService';

import { Messages } from '../../../../codegen/Localize';
import { DefinedBy, ISchedule, IScheduleFrequency } from '../../../../schedule/types/ISchedule';
import { IScheduleInfo } from '../../../../schedule/types/IScheduleInfo';

export function ScheduleFrequencyDescriptionCell(): React.Factory<BodyCell.Props> {
  return StyledCellFormatter(
      function ScheduleFrequencyDescriptionCell(props: BodyCell.Props) {

        const row = props.row;
        // Note: row contains more data than this
        type RemoteRefreshScheduleRowData = IScheduleFrequency & {
          timeZone: string;
        };
        const scheduleFrequency = row.data as RemoteRefreshScheduleRowData;
        const schedule: ISchedule = {
          id: '',
          name: '',
          scheduledAction: '',
          priority: 0,
          taskCount: 0,
          parallel: false,
          serverTimezoneOffset: 0,
          definedBy: DefinedBy.SELF_SERVICE,
          scheduleType: scheduleFrequency.scheduleType,
          scheduleDetails: scheduleFrequency.scheduleDetails,
          timeZoneDisplayName: scheduleFrequency.timeZone,
        };
        const scheduleDescription = IScheduleInfo.getScheduleDescription(schedule);
        return (
            <span title={scheduleDescription}>{scheduleDescription}</span>
        );
      }
  );
}

export function ScheduleDescriptionCellWithIcon(
    subscriptionConditionFn: (row: DataGrid.Row) => boolean,
    scheduleUrlFn: ((row: DataGrid.Row) => string) | null,
    subscriptionOnExtractRefreshFn: (row: DataGrid.Row) => boolean,
    accelerationTaskWithoutSchedule: (row: DataGrid.Row) => boolean
): React.Factory<BodyCell.Props> {
  return StyledCellFormatter(
      function ScheduleDescriptionCell(props: BodyCell.Props) {

        const row = props.row;
        const schedule = row.data.schedule as ISchedule;

        let cellStyle = {};
        let textStyles = {};
        let Icon: () => JSX.Element | undefined = undefined;

        if (subscriptionConditionFn(row)) {
          cellStyle = _.extend({cursor: 'default'}, CommonStyles.Layout.FlexBlockLeftAndCenter);
          textStyles = _.extend({display: 'block', flexShrink: 1}, CommonStyles.Layout.TextEllipsis);

          const iconStyle = {
            backgroundImage: `var(${ReactiveRawIconRegistry.register(NotificationInfoOutlineIconRaw, 16, applyColor(DesignTokens.color.font.default.secondary, '#666666'))})`,
            height: '35px',
            width: '35px',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat',
            marginLeft: '5px',
            flexShrink: 0
          };

          Icon = () => <span data-tb-test-id="data-grid-cell-icon"
                             style={iconStyle}
                             className={'data-present-class'}
                             title={Messages.column_subscriptionDataPresent()}/>;
        }

        if (subscriptionOnExtractRefreshFn(row)) {
          return (
              <span style={cellStyle}>
          <span style={textStyles}>
            <span
                title={Messages.subscriptionOnExtractRefreshSchedule()}>{Messages.subscriptionOnExtractRefreshSchedule()}</span>
          </span>
                {Icon && <Icon/>}
        </span>
          );
        }
        if (accelerationTaskWithoutSchedule(row)) {
          return (
              <span style={cellStyle}>
          <span style={textStyles}>
            <span title={Messages.accelerationOnWorkbookUpdates()}>{Messages.accelerationOnWorkbookUpdates()}</span>
          </span>
                {Icon && <Icon/>}
        </span>
          );
        } else {
          const scheduleDescription = IScheduleInfo.getScheduleDescription(schedule);

          const ScheduleCellContents = () => (
              <span>
            {scheduleUrlFn
                ? <a
                    href={scheduleUrlFn(row)}
                    title={schedule.name}
                    tabIndex={-1} // disable tabbing in datagrid cells, leaving focus managed by datagrid itself
                >
                  {schedule.name}
                </a>
                : <span title={schedule.name}>{schedule.name}</span>
            }
          </span>
          );

          const Dash = () => <span>&nbsp;&ndash;&nbsp;</span>;

          const selfService = schedule.definedBy === DefinedBy.SELF_SERVICE;
          //Exclude the dash and the schedule link if the schedule was created by self service

          return (
              <span style={cellStyle}>
            <span style={textStyles}>
                {selfService ? null : ScheduleCellContents()}
              {selfService ? null : Dash()}
              <span title={scheduleDescription}>{scheduleDescription}</span>
            </span>
                {Icon && <Icon/>}
          </span>
          );
        }
      }
  );
}
